/* import __COLOCATED_TEMPLATE__ from './sh-responsive-table.hbs'; */
import Component from '@ember/component';

export default Component.extend({

  /**
   * This is the css class that will be applied to the actual table element.  Multiple
   * classes should be presented in space-separted string format, ex: "class1 class2 class3"
   *
   * @property tableClass
   * @type String
   * @default undefined
   * @public
   */
  tableClass: undefined,

  /**
   * This is the css selector for the table, and must be provided.
   * Should generate a unique result from jQuery(selector).
   *
   * @property tableSelector
   * @type String
   * @default undefined
   * @public
   */
  tableSelector: undefined,

  /**
   * This is the list of column names that will be made available to each individual cell when the table
   * is viewed in mobile resolutions.  By default, the set of names reflect the text in the <th> cells from the table,
   * but if you'd like to use a different set of names for mobile, this is where you make that happen.  Accepts
   * either an array of values or an object with integer indexes (for greater specificity)
   *
   * @property headerCollection
   * @type Array|Object
   * @default undefined
   * @public
   */
  headerCollection: undefined,

  /**
   * This function gives you greater granularity over how your mobile column names are applied.  Function should
   * be of the type func(headerValue, headerIndex){}, and is called during each iteration of a loop through
   * headerCollection.  The function should return truthy if you'd like that index/value pair to be applied,
   * or falsey if not.
   *
   * Ex: Let's say you're intending to use the default headerCollection, which is just an array containing the
   * <th> cells from the table.  From those <th> values, you only want the 3rd column to actually carry through
   * to the mobile view.
   *
   * headerCollectionCallback = function(headerValue, headerIndex) {
   *    if(headerIndex === 2){
   *      return true;
   *    }
   *    return false;
   * }
   *
   * @property headerCollectionCallback
   * @type function
   * @default undefined
   * @public
   */
  headerCollectionCallback: undefined,

  /**
   * This is the text separator that will be used to separate the header value from the cell value in
   * mobile view.
   *
   * Ex:  Assuming you're just using the default headerCollection
   *
   * separator = '-=-'; // yields <th>.text-=-<td>.text
   *
   * @property separator
   * @type String
   * @default ':'
   * @public
   */
  separator: ':',

  didInsertElement(){
    let tableSelector = this.get('tableSelector');
    let headerCollection = this.get('headerCollection');

    if(!tableSelector) {
      return false;
    }

    let styleTag;

    if(!headerCollection){
      let table = this.element.querySelector(tableSelector);
      styleTag = this._buildStyleTag(table.querySelectorAll('th'));
    } else {
      styleTag = this._buildStyleTag(headerCollection);
    }

    document.body.appendChild(styleTag);
  },

  /**
   * Constructs the <style> tag/element containing the ::before <pseudo element> rules that enable the headerCollection
   * to render in the corresponding <td>'s at mobile resolutions.
   *
   * @method _buildStyleTag
   * @param headerValue - value of the current item passed from the headerCollection
   * @param iteratorIndex - the value of the index of the current item from the headerCollection
   * @private
   */
  _buildStyleTag(headerCollection) {
    let styleTag = '';
    let self = this;

    if(headerCollection.forEach !== undefined) { //it's an array
      headerCollection.forEach(function(header, headerIndex){
        styleTag += self._createRule(header.textContent, headerIndex);
      });
    } else { // it's an object
      for (let indexKey in headerCollection) {
        if(headerCollection.hasOwnProperty(indexKey)){
          styleTag += self._createRule(headerCollection[indexKey], parseInt(indexKey));
        }
      }
    }

    const styleNode = document.createElement('style');
    styleNode.textContent = styleTag;
    return styleNode;
  },

  /**
   * Accepts a value/index pair from an element in headerCollection.  The function first checks to see if a
   * headerCollectionCallback() function exists, and if it does, it tests the value/index pair for truthiness
   * when passed to the function.  If the function returns truthy (or the function doesn't exist), a CSS rule is
   * written that sets the corresponding <td>'s before::{} pseudo element's content value equal to the
   * passed-in headerValue.
   *
   * @method _createRule
   * @param headerValue - value of the current item passed from the headerCollection
   * @param iteratorIndex - the value of the index of the current item from the headerCollection
   * @private
   */
  _createRule(headerValue, iteratorIndex) {

    let headerCollectionCallback = this.get('headerCollectionCallback');

    if(headerCollectionCallback && typeof headerCollectionCallback === 'function') {
      if(!headerCollectionCallback(headerValue, iteratorIndex)){
        return '';
      }
    }

    let tableSelector = this.get('tableSelector');
    let separator = this.get('separator');

    // Since nthChild begins its index at 1 instead of 0, we have to add 1 to the passed in iteratorIndex
    let nthChildIndex = iteratorIndex + 1;
    let rule = `${tableSelector}.sh-responsive-table tr td:nth-child(${nthChildIndex})::before {
      content: "${headerValue}${separator}"
    }\n`;

    return rule;
  }
});
